import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useMyProfile } from '@/features/profile';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { useSessionStore } from '@/stores/session.store';

function FullScreenLoader(): JSX.Element {
  return (
    <div className="state" style={{ minHeight: '100vh' }}>
      <Spinner />
    </div>
  );
}

/**
 * Root routing decision: unauthenticated → login; authenticated but not
 * onboarded → onboarding; otherwise → the app shell.
 */
export function RootGate(): JSX.Element {
  const { t } = useTranslation();
  const status = useSessionStore((s) => s.status);
  const location = useLocation();
  const profile = useMyProfile();

  if (status === 'loading') return <FullScreenLoader />;

  if (status === 'unauthenticated') {
    return location.pathname === '/login' ? <Outlet /> : <Navigate to="/login" replace />;
  }

  if (profile.isPending) return <FullScreenLoader />;

  if (profile.isError) {
    return (
      <div className="state" style={{ minHeight: '100vh' }} role="alert">
        <p>{t('errors.generic')}</p>
        <Button variant="outline" onClick={() => void profile.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const onboarded = profile.data?.onboardingComplete ?? false;
  if (!onboarded) {
    return location.pathname === '/onboarding' ? <Outlet /> : <Navigate to="/onboarding" replace />;
  }

  if (location.pathname === '/login' || location.pathname === '/onboarding') {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
